<template>
  <div>
    <v-offline @detected-condition="connectionChange" />
    <section class="sec-main-layout">
      <div class="container" :style="getMainContainerStyle()">
        <div class="custom-header">
          <NetworkError />
          <div class="row">
            <div class="col-md-6 align-self-center">
              <div class="logo-wrapper">               
                <img src="../assets/logo.png" alt="Logo">
              </div>
            </div>
            <div class="col-md-6 align-self-center">
              <div class="language-wrapper">
                <LocaleChanger v-bind:emitRefresh="false" />
              </div>            
            </div>
          </div>
        </div>
        <div class="main-layout-wrapper">
          <div v-if="!horizontalLayout">
            <div class="row">
              <div class="col">
                <slot></slot>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <div v-if="showFacilitatorTools"><FacilitatorTools /></div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="row">
              <div class="col-xl-6">
                <slot />
              </div>
              <div class="col-xl-6">
                <div v-if="showFacilitatorTools"><FacilitatorTools /></div>
              </div>
            </div>
          </div>

          <NetworkError />
        </div>
      </div>
    </section>
    <div class="custom-footer">
      <Logo hide-when-no-company-logo />
      <p class="text-uppercase font-weight-bold">
        <small>
          <span v-if="$store.getters.getThisPlayer">{{ $t("player") }}: {{ $store.getters.getThisPlayer.nick_name }} -</span>
          {{ $t("version") }}: {{ gitVersion }}
        </small>
      </p>
    </div>
  </div>
</template>

<script>
import VOffline from "v-offline";
import { mapGetters } from "vuex";
import LocaleChanger from "@/components/LocaleChanger.vue";
import FacilitatorTools from "@/components/FacilitatorTools.vue";
import NetworkError from "@/components/NetworkError.vue";
import Logo from "@/components/Logo.vue";
import Swal from "sweetalert2";

// NB! CheckToken will not get called if FacilitatorTools are disabled.

// https://markus.oberlehner.net/blog/dynamic-vue-layout-components/

export default {
  name: "DefaultLayout",
  props: {
    facilitatorToolsBelow: {
      type: Boolean,
      required: false,
      default: false
    },
    disableFacilitatorTools: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: { LocaleChanger, FacilitatorTools, NetworkError, VOffline, Logo },
  metaInfo: {
    meta: [
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, maximum-scale=5, user-scalable=1"
      }
    ]
  },
  data: function() {
    return {};
  },
  updated() {},
  computed: {
    gitVersion() {
      if (process.env.VUE_APP_GIT_HASH) {
        return process.env.VUE_APP_GIT_HASH.substring(0, 7);
      }
      return "";
    },
    horizontalLayout() {
      // Horizontal layout is used if user is facilitator and component has requested to use horizontal
      return (
        !this.$store.state.main.playerIsObserver &&
        this.$store.getters.getIsFacilitator &&
        !this.facilitatorToolsBelow &&
        !this.disableFacilitatorTools
      );
    },
    showFacilitatorTools() {
      return (
        !this.$store.state.main.playerIsObserver &&
        !this.disableFacilitatorTools &&
        this.$store.getters.getIsFacilitator
      );
    },
    ...mapGetters(["getPlayers"])
  },
  watch: {
    getPlayers(newValue, oldValue) {
      if (
        !(
          this.$route.path.startsWith("/game") ||
          this.$route.path.startsWith("/selfevaluation") ||
          this.$route.path.startsWith("/begin") ||
          this.$route.path.startsWith("/end") ||
          this.$route.path.startsWith("/rps/lobby") ||
          this.$route.path.startsWith("/rps/begin") ||
          this.$route.path.startsWith("/rps/game") ||
          this.$route.path.startsWith("/rps/end")
        )
      ) {
        return;
      }

      if (this.$store.state.main.thisPlayerId > 0) {
        const exists = newValue.some(obj => obj.id === this.$store.state.main.thisPlayerId);

        if (!exists) {
          Swal.fire({
            text: this.$t("player-removed"),
            showCancelButton: false,
            confirmButtonText: this.$t("ok"),
            allowOutsideClick: false
          }).then(() => {
            this.$router.push("/gamecode/" + this.$store.state.main.game.gameCode);
          });
        }
      }
    }
  },
  created() {},
  methods: {
    getMainContainerStyle() {
      if (this.$store.getters.getIsFacilitator) {
        return "max-width: 95% !important;";
      } else if (this.$store.state.main.allPlayers.length > 15) {
        return "max-width: 1600px !important; ";
      } else {
        return "max-width: 1200px !important; ";
      }
    },
    connectionChange(onlineStatus) {
      this.$store.dispatch("log", "Online status changed: " + onlineStatus);
      if (onlineStatus) {
        // First load or return from offline -> request data update
        this.$store.dispatch("updateEverything");
      }
    }
  }
};
</script>

<i18n>
{
  "en": {
    "version": "Software version",
    "player": "Player",
    "player-removed": "You have been removed from the simulation. If you wish to continue in the simulation, you must create a new player and start the simulation from the beginning.",
    "ok": "OK"
  },
  "fi": {
    "version": "Ohjelmistoversio",
    "player": "Pelaaja",
    "player-removed": "Sinut on poistettu simulaatiosta. Jos haluat jatkaa simulaatiossa, täytyy sinun luoda uusi pelaaja ja aloittaa simulaatio alusta.",
    "ok": "OK"
  },
  "sv": {
    "version": "Programversion",
    "player": "Spelare",
    "player-removed": "Du har tagits bort från simulationen. Om du vill fortsätta i simulationen måste du skapa en ny spelare och börja om simulationen från början.",
    "ok": "OK"
  },
  "de": {
    "version": "Softwareversion",
    "player": "Spielerin",
    "player-removed": "Sie wurden aus der Simulation entfernt. Wenn Sie in der Simulation fortfahren möchten, müssen Sie einen neuen Spieler erstellen und die Simulation von vorne beginnen.",
    "ok": "OK"
  },
  "ko": {
    "version": "소프트웨어 버젼",
    "player": "플레이어",
    "player-removed": "당신은 시뮬레이션에서 제거되었습니다. 시뮬레이션을 계속하려면 새로운 플레이어를 만들고 시뮬레이션을 처음부터 다시 시작해야 합니다.",
    "ok": "네"
  }  
}
</i18n>
